/**
 * Support at Home Strategy Applicator
 *
 * Applies strategies to factFind to create modified scenarios for evaluation.
 * Always uses deep copying to prevent side effects.
 */
import type { FactFindAsset, FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { buildAnnuityImpact, annuityAssetFromRecord } from '../../../shared/src/calculation/annuityStrategies.ts';
import { calculateAge } from '../utils/index.ts';
import { applyDownsizeToFactFind } from '../features/supportAtHome/supportAtHomeFactFindMutators.tsx';
import {
    liquidateFundingSources,
    recordGiftInGiftsHistory,
    attachStrategyFundingLedger,
    getStrategyFundingLedger,
    buildDownsizeDetailsFromScenario,
    SAH_EMERGENCY_FUND_RESERVE,
    type FundingSource,
    type DownsizeDetails,
    type StrategyFundingLedgerEntry,
} from './supportAtHomeStrategyFunding.ts';
import { applySahEntityIpSaleToFactFind } from '../utils/supportAtHomeSuggestionEngineFunding.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../shared/src/calculation/support/capitalisingLoans.ts';
const MIN_ANNUITY_PURCHASE = 50000;

/**
 * A candidate strategy configuration, as produced by SupportAtHomeStrategyGenerator and consumed here.
 * Strategies don't share a single fixed shape server-side, so this models the union of fields actually
 * constructed by the generator / read by the applicator, keyed loosely off `type`.
 */
export interface StrategyCandidate {
    type: string;
    id?: string;
    name?: string;
    description?: string;
    // gifting / funeralBonds / annuity / debtRepayment funding
    amount?: number;
    perPersonAmount?: number;
    assetType?: string;
    assetId?: string | number;
    assetDescription?: string;
    fundingSources?: FundingSource[];
    ownership?: string;
    // annuity
    purchaseAmount?: number;
    // increaseHomeEquity / renovate / upgrade
    equityAmount?: number;
    renovationAmount?: number;
    additionalFundsNeeded?: number;
    currentHomeValue?: number;
    newHomeValue?: number;
    // downsize
    downsizeDetails?: DownsizeDetails;
    // reverseMortgage
    loanAmount?: number;
    loanPercentage?: number;
    interestRate?: number;
    loanStructure?: string;
    establishmentFees?: number;
    // heas
    paymentAmount?: number;
    paymentFrequency?: string;
    // debtRepayment
    liabilityKey?: string;
    selectedDebtKey?: string;
    repaymentAmount?: number;
    liabilityDescription?: string;
    // sellEntityInvestmentProperty
    investmentPropertyDetails?: Record<string, unknown>;
    structureFundingIncluded?: boolean;
    groupKey?: string;
    [key: string]: unknown;
}

/**
 * Apply a strategy to factFind
 */
export function applyStrategyToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const modified = structuredClone(factFind);

    switch (strategy.type) {
        case 'doNothing':
            return modified;
        /*
         * Several levers at once — the client-side mirror of the evaluator's own `combination` case
         * (server/src/rest/calculation/suggestionEngine/supportAtHome.ts), kept in step so a strategy
         * the engine ranked applies the same way when a screen applies it.
         *
         * The appliers compose without help: each liquidates from the fact-find it is handed, so step
         * two sees the assets step one already spent and a combination can never fund itself twice.
         * The funding ledger does NOT compose — each applier replaces it — so the lines are collected
         * after every step, or the summary would credit only whichever lever ran last.
         */
        case 'combination': {
            let working: DraftFactFind = modified;
            const ledger: StrategyFundingLedgerEntry[] = [];
            for (const step of (strategy.steps as StrategyCandidate[] | undefined) || []) {
                working = applyStrategyToFactFind(working, step);
                ledger.push(...getStrategyFundingLedger(working));
            }
            return attachStrategyFundingLedger(working, ledger);
        }
        case 'gifting':
            return applyGiftingToFactFind(modified, strategy);
        case 'funeralBonds':
            return applyFuneralBondToFactFind(modified, strategy);
        case 'annuity':
            return applyAnnuityStrategy(modified, strategy);
        case 'increaseHomeEquity':
        case 'renovate':
            return applyIncreaseHomeEquityToFactFind(modified, strategy);
        case 'downsize':
            return applyDownsizeStrategy(modified, strategy);
        case 'upgrade':
            return applyIncreaseHomeEquityToFactFind(modified, {
                ...strategy,
                equityAmount: strategy.additionalFundsNeeded ?? strategy.equityAmount ?? strategy.renovationAmount,
            });
        case 'debtRepayment':
            return applyDebtRepaymentToFactFind(modified, strategy);
        case 'reverseMortgage':
            return applyReverseMortgageToFactFind(modified, strategy);
        case 'heas':
            return applyHeasToFactFind(modified, strategy);
        case 'sellEntityInvestmentProperty':
            return applySellEntityInvestmentPropertyStrategy(factFind, modified, strategy);
        default:
            console.warn('Unknown strategy type:', strategy.type);
            return modified;
    }
}

function applyGiftingToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const requested = Math.max(0, strategy.amount || 0);
    if (requested <= 0) return factFind;

    const fundingSources = strategy.fundingSources || buildSingleSourceFunding(strategy);
    const { totalLiquidated, ledger } = liquidateFundingSources(factFind, fundingSources);
    const actualGift = Math.min(requested, totalLiquidated);
    if (actualGift <= 0) return attachStrategyFundingLedger(factFind, ledger);

    recordGiftInGiftsHistory(factFind, actualGift);
    ledger.push({ label: 'Gift to family', amount: actualGift, action: 'gifted' });
    return attachStrategyFundingLedger(factFind, ledger);
}

function applyFuneralBondToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const requested = Math.max(0, strategy.amount || 0);
    if (requested <= 0) return factFind;

    const fundingSources = strategy.fundingSources || buildSingleSourceFunding(strategy);
    const { totalLiquidated, ledger } = liquidateFundingSources(factFind, fundingSources);
    const actualPurchase = Math.min(requested, totalLiquidated);
    if (actualPurchase <= 0) return attachStrategyFundingLedger(factFind, ledger);

    if (!factFind.assets) factFind.assets = {};
    const existingBond = factFind.assets.funeralBonds?.find(
        (b) => b.ownership === (strategy.ownership || 'client')
    );

    if (!factFind.assets.funeralBonds) factFind.assets.funeralBonds = [];

    if (existingBond) {
        existingBond.value = (existingBond.value || 0) + actualPurchase;
    } else {
        factFind.assets.funeralBonds.push({
            id: `funeral-bond-${Date.now()}`,
            description: 'Prepaid Funeral Bond',
            value: actualPurchase,
            ownership: strategy.ownership || 'client',
            income: 0,
            growthRate: 0,
        });
    }

    ledger.push({ label: 'Funeral bond purchase', amount: actualPurchase, action: 'purchased' });
    return attachStrategyFundingLedger(factFind, ledger);
}

interface AnnuityRecord {
    id: string;
    annuityType?: string;
    purchaseAmount: number;
    owner?: string;
    annualPayment?: number;
    assessableIncome?: number;
    residualValue?: number;
    deductionAmount?: number;
    termYears?: number | null;
    assessableAsset?: number;
    warnings?: string[];
}

function addAnnuityAssetToFactFind(factFind: DraftFactFind, annuityRecord: AnnuityRecord): DraftFactFind {
    if (!factFind.assets) factFind.assets = {};

    const annuityAsset = annuityAssetFromRecord(annuityRecord, { ownership: annuityRecord.owner || 'client' }) as FactFindAsset;

    const annuities = (factFind.assets.annuities || []).filter((a) => a.id !== annuityRecord.id);
    annuities.push(annuityAsset);
    factFind.assets.annuities = annuities;
    return factFind;
}

function applyAnnuityStrategy(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const requested = Math.max(0, strategy.purchaseAmount || 0);
    const fundingSources = strategy.fundingSources || buildSingleSourceFunding(strategy);
    const { totalLiquidated, ledger } = liquidateFundingSources(factFind, fundingSources);
    const actualPurchase = Math.min(requested || totalLiquidated, totalLiquidated);

    if (actualPurchase < MIN_ANNUITY_PURCHASE) {
        return attachStrategyFundingLedger(factFind, ledger);
    }

    const ownerAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) ?? 70 : 70;
    const partnerAge = factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;

    // buildAnnuityImpact is the real (strict) calc-engine function — factFind's money fields are
    // guaranteed real numbers by this point (post-liquidation), so the Draft->strict cast is safe.
    const annuityRecord: AnnuityRecord = buildAnnuityImpact(factFind as unknown as FactFind, {
        id: strategy.id || `annuity-se-${Date.now()}`,
        annuityType: 'lifetime',
        purchaseAmount: actualPurchase,
        fundingSource: 'pre-liquidated',
        age: ownerAge,
        partnerAge,
        indexation: false,
        /* A CHOICE, NOT A CONSTANT. Centrelink counts a lifetime annuity at 60% of purchase price
           whatever its RCV, but net worth values it at its RESIDUAL — so a nil-RCV candidate loses
           the legacy ranking by the whole purchase price. Defaulting to 0 keeps every existing
           caller's behaviour; the generator now offers both ends. Mirrors the server applier. */
        residualCapital: Number.isFinite(Number(strategy.residualCapital)) ? Number(strategy.residualCapital) : 0,
        owner: strategy.ownership || 'client',
        reversionary: false,
    });

    if (!annuityRecord?.purchaseAmount) {
        return attachStrategyFundingLedger(factFind, ledger);
    }

    addAnnuityAssetToFactFind(factFind, annuityRecord);
    ledger.push({
        label: 'Lifetime annuity',
        amount: actualPurchase,
        action: 'purchased',
        assetType: 'annuities',
    });

    return attachStrategyFundingLedger(factFind, ledger);
}

function applyIncreaseHomeEquityToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const equityAmount = Math.max(
        0,
        (strategy.equityAmount ?? strategy.renovationAmount ?? strategy.additionalFundsNeeded) || 0
    );
    if (equityAmount <= 0) return factFind;

    const fundingSources = strategy.fundingSources || buildSingleSourceFunding(strategy);
    const { totalLiquidated, ledger } = liquidateFundingSources(factFind, fundingSources);
    const applied = Math.min(equityAmount, totalLiquidated);
    if (applied <= 0) return attachStrategyFundingLedger(factFind, ledger);

    if (!factFind.home) factFind.home = {};
    const currentHomeValue = factFind.home.value || 0;
    factFind.home.value = currentHomeValue + applied;

    ledger.push({ label: 'Home equity uplift', amount: applied, action: 'capitalised' });
    return attachStrategyFundingLedger(factFind, ledger);
}

function applySellEntityInvestmentPropertyStrategy(baselineFactFind: DraftFactFind, factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const details = strategy.investmentPropertyDetails;
    if (!details) return factFind;

    const result: DraftFactFind = applySahEntityIpSaleToFactFind(factFind, baselineFactFind, details);
    const ledger = [
        {
            label: strategy.name || 'Entity investment property sale',
            amount: parseFloat(String(details.salePrice)) || 0,
            action: 'sold',
            assetType: 'investmentProperty',
        },
    ];
    return attachStrategyFundingLedger(result, ledger);
}

function applyDownsizeStrategy(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const details: DownsizeDetails = strategy.downsizeDetails
        || buildDownsizeDetailsFromScenario(factFind, strategy);

    const result: DraftFactFind = applyDownsizeToFactFind(factFind, details);
    const ledger: StrategyFundingLedgerEntry[] = [{
        label: 'Family home',
        amount: factFind.home?.value || 0,
        action: 'downsized',
        assetType: 'home',
    }];

    if (details.downsizerContribution > 0) {
        ledger.push({
            label: details.commenceIncomeStream ? 'Client downsizer to ABP' : 'Client downsizer to super',
            amount: details.downsizerContribution,
            action: 'contributed',
        });
    }
    if (details.partnerDownsizerContribution > 0) {
        ledger.push({
            label: details.commenceIncomeStream ? 'Partner downsizer to ABP' : 'Partner downsizer to super',
            amount: details.partnerDownsizerContribution,
            action: 'contributed',
        });
    }
    if (details.debtRepayment > 0) {
        ledger.push({ label: 'Debt repayment from proceeds', amount: details.debtRepayment, action: 'repaid' });
    }

    return attachStrategyFundingLedger(result, ledger);
}

function applyDebtRepaymentToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const requested = Math.max(0, strategy.repaymentAmount || 0);
    const liabilityKey = strategy.liabilityKey || strategy.selectedDebtKey;
    if (requested <= 0 || !liabilityKey) return factFind;

    const fundingSources = strategy.fundingSources || buildSingleSourceFunding(strategy);
    const { totalLiquidated, ledger } = liquidateFundingSources(factFind, fundingSources);
    const repay = Math.min(requested, totalLiquidated);
    if (repay <= 0) return attachStrategyFundingLedger(factFind, ledger);

    const [liabilityType, liabilityId] = liabilityKey.split(':');
    const liabilityList = factFind.liabilities?.[liabilityType];
    if (Array.isArray(liabilityList)) {
        const target = liabilityList.find((item) => String(item.id) === String(liabilityId));
        if (target) {
            const currentBalance = Math.max(0, target.balance ?? target.value ?? 0);
            const newBalance = Math.max(0, currentBalance - repay);
            if (target.balance !== undefined) target.balance = newBalance;
            else target.value = newBalance;

            ledger.push({
                label: target.description || 'Debt repayment',
                amount: repay,
                action: 'repaid',
                assetType: 'liabilities',
            });
        }
    }

    return attachStrategyFundingLedger(factFind, ledger);
}

function buildSingleSourceFunding(strategy: StrategyCandidate): FundingSource[] {
    const amount = (strategy.amount ?? strategy.purchaseAmount ?? strategy.renovationAmount ?? strategy.repaymentAmount) || 0;
    if (!strategy.assetType || !strategy.assetId || amount <= 0) return [];
    return [{ type: strategy.assetType, id: strategy.assetId, amountToTake: amount }];
}

function applyReverseMortgageToFactFind(factFind: DraftFactFind, strategy: StrategyCandidate): DraftFactFind {
    const loanAmount = strategy.loanAmount || 0;
    const establishmentFees = strategy.establishmentFees || 0;
    const netProceeds = Math.max(0, loanAmount - establishmentFees);

    if (!factFind.assets) factFind.assets = {};

    if (strategy.loanStructure !== 'regular_payments' && netProceeds > 0) {
        if (!factFind.assets.bankAccounts) factFind.assets.bankAccounts = [];
        if (factFind.assets.bankAccounts.length > 0) {
            const first = factFind.assets.bankAccounts[0];
            first.value = (first.value || 0) + netProceeds;
        } else {
            factFind.assets.bankAccounts.push({
                id: 'reverse-mortgage-proceeds',
                type: 'bankAccounts',
                description: 'Reverse Mortgage Proceeds',
                value: netProceeds,
                ownership: 'client',
                // Interest, not capital growth — see bankPlugin.ts's DEFAULT_BANK_GROWTH_RATE_PERCENT.
                incomeRate: 2,
                growthRate: 0,
            });
        }
    }

    if (loanAmount > 0) {
        factFind.liabilities = factFind.liabilities || {};
        factFind.liabilities.loans = factFind.liabilities.loans || [];
        const existing = factFind.liabilities.loans.find((l) => l.id === 'reverse-mortgage');
        const liability = {
            id: 'reverse-mortgage',
            type: 'reverse_mortgage',
            description: 'Reverse Mortgage',
            balance: loanAmount,
            interestRate: strategy.interestRate || REVERSE_MORTGAGE_DEFAULT_RATE,
            repayments: 0,
            excludeFromCashflow: true,
        };
        if (existing) Object.assign(existing, liability);
        else factFind.liabilities.loans.push(liability);
    }

    return factFind;
}

function applyHeasToFactFind(factFind: DraftFactFind, _strategy: StrategyCandidate): DraftFactFind {
    // Starting HEAS changes NOTHING on the day-one balance sheet: payments arrive fortnightly over
    // time, the loan starts at zero and accrues as they do, and the payments are not means-tested
    // income — all modelled by the projection layer via heas strategy options. This used to deduct
    // a year of payments from the first bank account (a phantom withdrawal that inflated the
    // pension and understated the estate); fixed 2026-08-14 alongside the server evaluator — the
    // two must stay in lockstep or saved strategies re-materialise on different balance sheets.
    return factFind;
}

export function applyStrategyCombinationToFactFind(factFind: DraftFactFind, strategies: StrategyCandidate[]): DraftFactFind {
    if (strategies.length === 0) return structuredClone(factFind);
    let modified = factFind;
    strategies.forEach((strategy) => {
        modified = applyStrategyToFactFind(modified, strategy);
    });
    return modified;
}

export { SAH_EMERGENCY_FUND_RESERVE };
